/**
 * IndexedDB keystore for E2E private material + DR sessions.
 */

const DB_NAME = 'lynx-e2e';
const DB_VERSION = 1;
const STORE_META = 'meta';
const STORE_OTP = 'oneTimePrekeys';
const STORE_SESSIONS = 'sessions';

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
      if (!db.objectStoreNames.contains(STORE_OTP)) {
        db.createObjectStore(STORE_OTP, { keyPath: 'keyId' });
      }
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'chatId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('idb open failed'));
  });
  return dbPromise;
};

const withStore = async (storeName, mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result;
    try {
      result = fn(store);
    } catch (err) {
      reject(err);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error || new Error('idb tx failed'));
    tx.onabort = () => reject(tx.error || new Error('idb tx aborted'));
  });
};

const idbReq = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('idb request failed'));
});

export const getMeta = async (key) => {
  const db = await openDb();
  return idbReq(db.transaction(STORE_META, 'readonly').objectStore(STORE_META).get(key));
};

export const setMeta = async (key, value) => {
  await withStore(STORE_META, 'readwrite', (store) => {
    store.put(value, key);
  });
};

export const deleteMeta = async (key) => {
  await withStore(STORE_META, 'readwrite', (store) => {
    store.delete(key);
  });
};

export const putOneTimePrekeys = async (rows) => {
  await withStore(STORE_OTP, 'readwrite', (store) => {
    rows.forEach((row) => store.put(row));
  });
};

export const takeOneTimePrekey = async (keyId) => {
  const db = await openDb();
  const tx = db.transaction(STORE_OTP, 'readwrite');
  const store = tx.objectStore(STORE_OTP);
  const row = await idbReq(store.get(Number(keyId)));
  if (row) {
    store.delete(Number(keyId));
  }
  await new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return row || null;
};

/** Read OTP without consuming — consume only after successful decrypt. */
export const peekOneTimePrekey = async (keyId) => {
  const db = await openDb();
  return idbReq(db.transaction(STORE_OTP, 'readonly').objectStore(STORE_OTP).get(Number(keyId)));
};

export const removeOneTimePrekey = async (keyId) => {
  await withStore(STORE_OTP, 'readwrite', (store) => {
    store.delete(Number(keyId));
  });
};

export const countOneTimePrekeys = async () => {
  const db = await openDb();
  return idbReq(db.transaction(STORE_OTP, 'readonly').objectStore(STORE_OTP).count());
};

export const getSession = async (chatId) => {
  const db = await openDb();
  return idbReq(db.transaction(STORE_SESSIONS, 'readonly').objectStore(STORE_SESSIONS).get(String(chatId)));
};

export const putSession = async (chatId, state) => {
  await withStore(STORE_SESSIONS, 'readwrite', (store) => {
    store.put({ chatId: String(chatId), state, updatedAt: Date.now() });
  });
};

export const deleteSession = async (chatId) => {
  await withStore(STORE_SESSIONS, 'readwrite', (store) => {
    store.delete(String(chatId));
  });
};

export const clearAllSessions = async () => {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readwrite');
    tx.objectStore(STORE_SESSIONS).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export const clearAllE2E = async () => {
  const db = await openDb();
  await Promise.all([STORE_META, STORE_OTP, STORE_SESSIONS].map((name) => (
    new Promise((resolve, reject) => {
      const tx = db.transaction(name, 'readwrite');
      tx.objectStore(name).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    })
  )));
};

export const E2E_CHANGED_EVENT = 'lynx-e2e-changed';

export const notifyE2EChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(E2E_CHANGED_EVENT));
};

export const subscribeE2EChanged = (handler) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(E2E_CHANGED_EVENT, handler);
  return () => window.removeEventListener(E2E_CHANGED_EVENT, handler);
};
